import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Server, Globe, Lock, Pencil, Trash2, Check, Plus, Wallet } from 'lucide-react';
import api from '../services/api.js';

// Сумма ₽ в человекочитаемый вид (1234.5 → "1 234,5 ₽").
const fmtMoney = (v) =>
  v == null ? null : `${Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('ru-RU');
};

const CATEGORIES = [
  { key: 'server', title: 'Серверы', icon: Server },
  { key: 'domen', title: 'Домены', icon: Globe },
  { key: 'ssl', title: 'SSL-сертификаты', icon: Lock },
];

// day_payment в БД: "DD.MM.YYYY H:MM" → дата для <input type=date> (YYYY-MM-DD).
const payToInputDate = (str) => {
  const m = String(str || '').trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (!m) return '';
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
};

// YYYY-MM-DD → "DD.MM.YYYY 0:00" (как хранит бот).
const inputDateToPay = (iso) => {
  if (!iso) return '';
  const [yyyy, mm, dd] = iso.split('-');
  return `${dd}.${mm}.${yyyy} 0:00`;
};

const tierFor = (d) => (d == null ? 'none' : d <= 0 ? 'crit' : d <= 5 ? 'warn' : 'ok');

const TIER_BADGE = {
  none: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  ok: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  warn: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  crit: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const daysLabel = (n) => {
  if (n == null) return 'нет даты';
  if (n < 0) return `просрочено на ${Math.abs(n)} дн.`;
  if (n === 0) return 'оплата сегодня';
  return `${n} дн.`;
};

const ServerPaymentsModal = ({ open, onClose }) => {
  const [data, setData] = useState({ server: [], domen: [], ssl: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // edit = { category, originalName }, add = category
  const [edit, setEdit] = useState(null);
  const [addCat, setAddCat] = useState(null);
  const [form, setForm] = useState({ name: '', date: '' });
  // renew = { category, name }
  const [renew, setRenew] = useState(null);
  const [renewForm, setRenewForm] = useState({ amount: '', period: '1', unit: 'months' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/server-payments');
      setData(res.data?.data || { server: [], domen: [], ssl: [] });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      setEdit(null);
      setAddCat(null);
      setRenew(null);
      setForm({ name: '', date: '' });
    }
  }, [open, load]);

  const startEdit = (category, row) => {
    setAddCat(null);
    setRenew(null);
    setEdit({ category, originalName: row.name });
    setForm({ name: row.name || '', date: payToInputDate(row.day_payment) });
  };

  const startAdd = (category) => {
    setEdit(null);
    setRenew(null);
    setAddCat(category);
    setForm({ name: '', date: '' });
  };

  const cancel = () => {
    setEdit(null);
    setAddCat(null);
    setRenew(null);
    setForm({ name: '', date: '' });
  };

  const startRenew = (category, row) => {
    setEdit(null);
    setAddCat(null);
    setRenew({ category, name: row.name });
    setRenewForm({ amount: '', period: '1', unit: 'months' });
  };

  const saveRenew = async () => {
    const amount = Number(renewForm.amount);
    const period = Number(renewForm.period);
    if (!(amount > 0) || !(period > 0)) return;
    setSaving(true);
    try {
      const body = { amount };
      if (renewForm.unit === 'months') body.months = period;
      else body.days = period;
      await api.post(
        `/server-payments/${renew.category}/${encodeURIComponent(renew.name)}/renew`,
        body
      );
      cancel();
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка продления');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!form.name || !form.date) return;
    setSaving(true);
    try {
      await api.put(`/server-payments/${edit.category}/${encodeURIComponent(edit.originalName)}`, {
        name: form.name,
        day_payment: inputDateToPay(form.date),
      });
      cancel();
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const saveAdd = async () => {
    if (!form.name || !form.date) return;
    setSaving(true);
    try {
      await api.post(`/server-payments/${addCat}`, {
        name: form.name,
        day_payment: inputDateToPay(form.date),
      });
      cancel();
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка добавления');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (category, name) => {
    if (!window.confirm(`Удалить «${name}»?`)) return;
    try {
      await api.delete(`/server-payments/${category}/${encodeURIComponent(name)}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка удаления');
    }
  };

  const inputCls =
    'w-full px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';

  const formRow = (onSave, saveLabel) => (
    <div className="rounded-xl border border-primary-200 dark:border-primary-700/40 bg-primary-50/50 dark:bg-primary-900/10 p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="Название"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className={inputCls}
        />
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          className={inputCls}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={cancel} className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600">
          Отмена
        </button>
        <button
          onClick={onSave}
          disabled={saving || !form.name || !form.date}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40"
        >
          <Check size={16} /> {saveLabel}
        </button>
      </div>
    </div>
  );

  // Форма «Оплатить и продлить»: сумма ₽ + срок (мес/дней). Продлевает срок и
  // одной операцией пишет расход в бухгалтерию.
  const renewRowForm = () => (
    <div className="rounded-xl border border-emerald-200 dark:border-emerald-700/40 bg-emerald-50/50 dark:bg-emerald-900/10 p-3 space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-[1fr_5rem_7rem] gap-2">
        <input
          type="number" min="0" step="0.01" inputMode="decimal"
          placeholder="Сумма, ₽"
          value={renewForm.amount}
          onChange={(e) => setRenewForm((f) => ({ ...f, amount: e.target.value }))}
          className={inputCls}
          autoFocus
        />
        <input
          type="number" min="1" step="1"
          placeholder="Срок"
          value={renewForm.period}
          onChange={(e) => setRenewForm((f) => ({ ...f, period: e.target.value }))}
          className={inputCls}
        />
        <select
          value={renewForm.unit}
          onChange={(e) => setRenewForm((f) => ({ ...f, unit: e.target.value }))}
          className={inputCls}
        >
          <option value="months">мес.</option>
          <option value="days">дней</option>
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={cancel} className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600">
          Отмена
        </button>
        <button
          onClick={saveRenew}
          disabled={saving || !(Number(renewForm.amount) > 0) || !(Number(renewForm.period) > 0)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
        >
          <Wallet size={16} /> Оплатить и продлить
        </button>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.18 }}
            className="relative z-10 w-full max-w-3xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-t-2xl">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                <Server size={18} className="text-primary-600 dark:text-primary-400" />
                Сроки оплаты
              </h3>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Закрыть"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto px-3 sm:px-5 py-3 space-y-5">
              {error && <div className="text-center py-2 text-red-500 text-sm">{error}</div>}
              {loading && <div className="text-center py-8 text-gray-500 dark:text-gray-400">Загрузка…</div>}

              {!loading &&
                CATEGORIES.map(({ key, title, icon: Icon }) => {
                  const rows = data[key] || [];
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          <Icon size={16} /> {title}
                          <span className="text-xs font-normal text-gray-400">{rows.length}</span>
                        </h4>
                        <button
                          onClick={() => startAdd(key)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700"
                        >
                          <Plus size={14} /> Добавить
                        </button>
                      </div>

                      {addCat === key && <div className="mb-2">{formRow(saveAdd, 'Добавить')}</div>}

                      <div className="space-y-2">
                        {rows.length === 0 && addCat !== key && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 py-1">Нет записей.</p>
                        )}
                        {rows.map((row) => {
                          const isEditing = edit && edit.category === key && edit.originalName === row.name;
                          if (isEditing) return <div key={row.name}>{formRow(saveEdit, 'Сохранить')}</div>;
                          const isRenewing = renew && renew.category === key && renew.name === row.name;
                          const tier = tierFor(row.days_left);
                          return (
                            <div
                              key={row.name}
                              className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-3"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{row.name}</span>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${TIER_BADGE[tier]}`}>
                                      {daysLabel(row.days_left)}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    продление: {row.day_payment || '—'}
                                  </div>
                                  {row.last_amount != null && (
                                    <div className="mt-0.5 text-xs text-emerald-600 dark:text-emerald-400">
                                      оплата: {fmtMoney(row.last_amount)}
                                      {row.last_paid_date ? ` · ${fmtDate(row.last_paid_date)}` : ''}
                                      {row.total_spent != null && row.payments_count > 1
                                        ? ` · всего ${fmtMoney(row.total_spent)}`
                                        : ''}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <button
                                    onClick={() => startRenew(key, row)}
                                    title="Оплатить и продлить"
                                    className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                  >
                                    <Wallet size={16} />
                                  </button>
                                  <button
                                    onClick={() => startEdit(key, row)}
                                    title="Редактировать"
                                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  <button
                                    onClick={() => remove(key, row.name)}
                                    title="Удалить"
                                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                              {isRenewing && <div className="mt-3">{renewRowForm()}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ServerPaymentsModal;
